import { Attribute } from '@/utils/types/collections'

export const BITMAP_NUMERICAL_ORDER_TRAIT = 'Numerical order'
export const BITMAP_EPOCH_TRAIT = 'Epoch'

// Halving epochs, the way bitmap.trade sections its explorer.
export const BITMAP_EPOCH_VALUES = [
	{ value: 'Genesis', min: 0, max: 209_999 },
	{ value: 'Halving I', min: 210_000, max: 419_999 },
	{ value: 'Halving II', min: 420_000, max: 629_999 },
	{ value: 'Halving III', min: 630_000, max: 839_999 },
	{ value: 'Halving IV', min: 840_000, max: 1_049_999 }
]

export const BITMAP_NUMERICAL_ORDER_VALUES = [
	{ value: 'sub 100', limit: 100 },
	{ value: 'sub 1k', limit: 1_000 },
	{ value: 'sub 10k', limit: 10_000 },
	{ value: 'sub 100k', limit: 100_000 },
	{ value: 'sub 500k', limit: 500_000 },
	{ value: 'sub 1m', limit: 1_000_000 }
]

type SelectedFilter = {
	trait_type: string
	values: string[]
}

const normalizeBitmapFilterValue = (value: string) => value.toLowerCase().replace(/\s+/g, '')

export function buildBitmapFilterAttributes(
	attributes: Attribute[] = [],
	supply?: number
): Attribute[] {
	const numericalOrder = {
		trait_type: BITMAP_NUMERICAL_ORDER_TRAIT,
		values: BITMAP_NUMERICAL_ORDER_VALUES.map(({ value, limit }) => ({
			value,
			count: Math.min(limit, supply || limit)
		}))
	}

	const epoch = {
		trait_type: BITMAP_EPOCH_TRAIT,
		values: BITMAP_EPOCH_VALUES.map(({ value, min, max }) => ({
			value,
			count: Math.max(0, Math.min(max, (supply || max) - 1) - min + 1)
		}))
	}

	const rest = attributes.filter(
		(attribute) =>
			attribute?.trait_type !== BITMAP_NUMERICAL_ORDER_TRAIT &&
			attribute?.trait_type !== BITMAP_EPOCH_TRAIT
	)

	return [numericalOrder, epoch, ...rest]
}

function isBitmapNumericalOrderFilter(filter?: SelectedFilter) {
	return (
		filter?.trait_type === BITMAP_NUMERICAL_ORDER_TRAIT ||
		filter?.trait_type === BITMAP_EPOCH_TRAIT ||
		filter?.trait_type === 'Range'
	)
}

export function splitBitmapFilters(filters: SelectedFilter[] = []) {
	return filters.reduce(
		(acc, filter) => {
			if (isBitmapNumericalOrderFilter(filter)) {
				acc.numberFilters.push(filter)
			} else {
				acc.attributeFilters.push(filter)
			}
			return acc
		},
		{ numberFilters: [] as SelectedFilter[], attributeFilters: [] as SelectedFilter[] }
	)
}

function getBitmapNumberLimit(value: string) {
	const normalized = normalizeBitmapFilterValue(value)
	return BITMAP_NUMERICAL_ORDER_VALUES.find(
		(option) => normalizeBitmapFilterValue(option.value) === normalized
	)?.limit
}

function parseBitmapNumber(name?: string | null) {
	if (!name) return null
	const match = name.match(/^(\d+)\.bitmap$/i)
	if (!match) return null
	const value = Number(match[1])
	return Number.isFinite(value) ? value : null
}

function getBitmapNumberFromItem(item: any) {
	return parseBitmapNumber(item?.meta?.name || item?.escrow?.name || item?.name)
}

function getBitmapEpochRange(value: string) {
	const normalized = normalizeBitmapFilterValue(value)
	return BITMAP_EPOCH_VALUES.find(
		(option) => normalizeBitmapFilterValue(option.value) === normalized
	)
}

/**
 * Block-number filters are evaluated client-side: "sub N" values combine to
 * one upper bound, epoch values to a set of allowed ranges, and an item must
 * satisfy both groups.
 */
export function matchesBitmapNumberFilters(item: any, filters: SelectedFilter[] = []) {
	const limits = filters
		.filter((filter) => filter.trait_type !== BITMAP_EPOCH_TRAIT)
		.flatMap((filter) => filter.values || [])
		.map(getBitmapNumberLimit)
		.filter((limit): limit is number => typeof limit === 'number')
	const epochs = filters
		.filter((filter) => filter.trait_type === BITMAP_EPOCH_TRAIT)
		.flatMap((filter) => filter.values || [])
		.map(getBitmapEpochRange)
		.filter((range): range is (typeof BITMAP_EPOCH_VALUES)[number] => !!range)

	if (limits.length === 0 && epochs.length === 0) return true

	const bitmapNumber = getBitmapNumberFromItem(item)
	if (bitmapNumber === null) return false

	if (limits.length > 0 && !(bitmapNumber < Math.max(...limits))) return false
	if (epochs.length > 0 && !epochs.some((e) => bitmapNumber >= e.min && bitmapNumber <= e.max))
		return false
	return true
}

// ── Quick filters (the explorer chips) ───────────────────────────────────────
// bitmap.trade's explorer offers named chips over the same on-chain facts our
// indexer already exposes as traits. Each chip is a set of trait:value pairs;
// the API ORs attribute pairs, so a chip with several members (Legendary,
// Punks) is a union on the server and matched with `some` on the client.

export type BitmapQuickFilterMember = { trait_type: string; value: string }
export type BitmapQuickFilter = {
	id: string
	label: string
	hint: string
	group: 'historical' | 'punk' | 'numeric' | 'rarity'
	members: BitmapQuickFilterMember[]
	/** members grouped by trait, the shape updateFilters wants */
	byTrait: Record<string, string[]>
}

export const BITMAP_LEGENDARY_TRAIT = 'Legendary'

/** The storied one-offs: the pizza blocks, Satoshi's blocks, the maths-y heights. */
export const BITMAP_LEGENDARY_MEMBERS: BitmapQuickFilterMember[] = [
	{ trait_type: 'pizza', value: 'true' },
	{ trait_type: 'nakamoto', value: 'true' },
	{ trait_type: 'fibonacci', value: 'true' },
	{ trait_type: 'sameDigits', value: 'true' },
	{ trait_type: 'sequential', value: 'true' },
	{ trait_type: 'blockRarity800K', value: 'epic' }
]

const PUNK_VALUES = [
	'pristinePunks',
	'5txPerfectPunks',
	'gridPunks',
	'standardPunks',
	'wideNeckPunks',
	'communityPunks'
]

function quick(
	id: string,
	label: string,
	hint: string,
	group: BitmapQuickFilter['group'],
	members: BitmapQuickFilterMember[]
): BitmapQuickFilter {
	const byTrait: Record<string, string[]> = {}
	for (const m of members) (byTrait[m.trait_type] ||= []).push(m.value)
	return { id, label, hint, group, members, byTrait }
}

export const BITMAP_QUICK_FILTERS: BitmapQuickFilter[] = [
	quick(
		'legendary',
		'Legendary',
		'Pizza, Nakamoto, Fibonacci, same-digit, sequential and epic blocks',
		'historical',
		[{ trait_type: BITMAP_LEGENDARY_TRAIT, value: 'true' }]
	),
	quick('pizza', 'Pizza Block', 'The 10,000 BTC pizza transaction blocks', 'historical', [
		{ trait_type: 'pizza', value: 'true' }
	]),
	quick('patoshi', 'Patoshi', 'Early Patoshi-pattern miner blocks', 'historical', [
		{ trait_type: 'patoshi', value: 'true' }
	]),
	quick('nakamoto', 'Nakamoto', 'Blocks mined by Satoshi Nakamoto', 'historical', [
		{ trait_type: 'nakamoto', value: 'true' }
	]),
	quick('vintage', 'Vintage', 'The first 1,000 blocks', 'historical', [
		{ trait_type: 'vintage', value: 'true' }
	]),
	quick(
		'punks',
		'Punks',
		'Blocks whose layout renders as a pixel avatar',
		'punk',
		PUNK_VALUES.map((value) => ({ trait_type: 'punks', value }))
	),
	quick('perfect-punk', 'Perfect Punk', 'Five-transaction perfectly formed avatars', 'punk', [
		{ trait_type: 'punks', value: '5txPerfectPunks' }
	]),
	quick('pristine-punk', 'Pristine Punk', 'Highest-fidelity avatar layouts', 'punk', [
		{ trait_type: 'punks', value: 'pristinePunks' }
	]),
	quick('same-digits', 'Same Digits', 'Block height made of one repeated digit', 'numeric', [
		{ trait_type: 'sameDigits', value: 'true' }
	]),
	quick('palindrome', 'Palindrome', 'Block height reads the same backwards', 'numeric', [
		{ trait_type: 'palindromic', value: 'true' }
	]),
	quick('sequential', 'Sequential', 'Block height with consecutive digits', 'numeric', [
		{ trait_type: 'sequential', value: 'true' }
	]),
	quick('fibonacci', 'Fibonacci', 'Block height is a Fibonacci number', 'numeric', [
		{ trait_type: 'fibonacci', value: 'true' }
	]),
	quick('sub-100k', 'Sub 100k', 'The first 100,000 blocks', 'numeric', [
		{ trait_type: BITMAP_NUMERICAL_ORDER_TRAIT, value: 'sub 100k' }
	]),
	quick('epic', 'Epic', 'Epic rarity at the 800k milestone', 'rarity', [
		{ trait_type: 'blockRarity800K', value: 'epic' }
	]),
	quick('rare', 'Rare', 'Rare rarity at the 800k milestone', 'rarity', [
		{ trait_type: 'blockRarity800K', value: 'rare' }
	])
]

/** Human labels for the raw trait keys the indexer uses. */
export const BITMAP_TRAIT_LABELS: Record<string, string> = {
	[BITMAP_LEGENDARY_TRAIT]: 'Legendary',
	pizza: 'Pizza Block',
	patoshi: 'Patoshi',
	nakamoto: 'Nakamoto',
	vintage: 'Vintage',
	punks: 'Punks',
	sameDigits: 'Same Digits',
	palindromic: 'Palindrome',
	sequential: 'Sequential',
	fibonacci: 'Fibonacci',
	blockRarity800K: '800k Rarity',
	nTx: 'Transactions',
	difficulty: 'Difficulty',
	weight: 'Weight',
	year: 'Year'
}

export function bitmapTraitLabel(trait_type: string) {
	return BITMAP_TRAIT_LABELS[trait_type] || trait_type
}

export function isBitmapQuickFilterActive(q: BitmapQuickFilter, filters: SelectedFilter[] = []) {
	return Object.entries(q.byTrait).every(([trait, values]) => {
		const current = filters.find((f) => f.trait_type === trait)?.values || []
		return values.every((v) => current.includes(v))
	})
}

/**
 * The `trait:value` pairs to send to the API for a set of attribute filters,
 * with the Legendary pseudo-trait expanded to its member pairs.
 */
export function expandBitmapAttributeFilters(filters: SelectedFilter[] = []): string[] {
	return filters
		.filter((f) => f.trait_type !== 'Satributes')
		.flatMap((f) =>
			f.trait_type === BITMAP_LEGENDARY_TRAIT
				? BITMAP_LEGENDARY_MEMBERS.map((m) => `${m.trait_type}:${m.value}`)
				: (f.values || []).map((v) => `${f.trait_type}:${v}`)
		)
}

/**
 * Client-side check for one attribute filter against an item's traits: a
 * plain trait needs one of its values; Legendary needs any member pair.
 */
export function matchesBitmapAttributeFilter(
	filter: SelectedFilter,
	attrs: { trait_type?: string; value?: string }[]
) {
	if (filter.trait_type === BITMAP_LEGENDARY_TRAIT) {
		return BITMAP_LEGENDARY_MEMBERS.some((m) =>
			attrs.some((a) => a?.trait_type === m.trait_type && a?.value === m.value)
		)
	}
	const attr = attrs.find((a) => a?.trait_type === filter.trait_type)
	return !!attr && (filter.values || []).includes(attr.value as string)
}

const PUNK_LABELS: Record<string, string> = {
	pristinePunks: 'Pristine',
	'5txPerfectPunks': 'Perfect',
	gridPunks: 'Grid',
	standardPunks: 'Standard',
	wideNeckPunks: 'Wide neck',
	communityPunks: 'Community'
}

/** "Pizza Block", "Punks: Pristine, Grid", "Epoch: Halving IV" — for the active-filter line. */
export function bitmapFilterSummary(trait_type: string, values: string[] = []) {
	const label = bitmapTraitLabel(trait_type)
	if (values.length === 1 && values[0] === 'true') return label
	if (trait_type === 'punks') {
		const all = values.length >= Object.keys(PUNK_LABELS).length
		return all ? label : `${label}: ${values.map((v) => PUNK_LABELS[v] || v).join(', ')}`
	}
	return `${label}: ${values.join(', ')}`
}
